import FormInput from "@components/Form/FormInput";
import FormLabel from "@components/Form/FormLabel";
import FormTextarea from "@components/Form/FormTextarea";
import { StyledButton } from "@components/Styled/Button";
import { StyledH2, StyledP, StyledXSmall } from "@components/Styled/Text";
import React from "react";
import FitFeedback from "./FitFeedback";
import { withReviewsForm } from "./withReviewsForm";

import { clsx } from "clsx";

const Wrapper = (props: any) => (
  <form
    {...props}
    className={clsx(
      props.className,
      "flex w-full flex-wrap px-midi text-hint md:px-zero md:text-body",
    )}
  />
);

const Header = (props: any) => (
  <StyledH2
    {...props}
    className={clsx(
      props.className,
      "mb-midi w-full text-center md:text-[44px]",
    )}
  />
);

const Column = (props: any) => (
  <div
    {...props}
    className={clsx(props.className, "w-full md:w-1/2 md:px-mini")}
  />
);

const Button = (props: any) => (
  <StyledButton
    {...props}
    className={clsx(props.className, "md:max-w-33 w-full")}
  />
);

const Input = (props: any) => (
  <FormInput
    {...props}
    className={clsx(
      props.className,
      "mb-midi h-auto px-zero",
      props.error &&
        "mb-zero [&_input]:border-b-2 [&_input]:border-warning [&_input]:text-warning",
    )}
  />
);

const Label = (props: any) => (
  <FormLabel
    {...props}
    className={clsx(props.className, "pb-micro text-primary")}
  />
);

const Textarea = (props: any) => (
  <FormTextarea
    {...props}
    className={clsx(
      props.className,
      "mb-midi px-zero",
      props.error &&
        "mb-zero [&_textarea]:border-b-2 [&_textarea]:border-warning [&_textarea]:text-warning",
    )}
  />
);

const TextareaWrapper = (props: any) => (
  <div {...props} className={clsx(props.className, "w-full md:px-mini")} />
);

const ReviewTitle = (props: any) => (
  <FormInput
    {...props}
    className={clsx(props.className, "mb-midi w-full px-zero md:px-mini")}
  />
);

const Rating = (props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "flex w-full flex-wrap px-zero md:px-mini",
    )}
  />
);

const Stars = (props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "text-orange mb-maxi flex w-full flex-col justify-start md:mb-zero md:w-1/2",
    )}
  />
);

const ButtonWrapper = (props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "flex w-full items-center justify-center md:w-1/2",
    )}
  />
);

const ErrorMessage = (props: any) => (
  <StyledXSmall
    {...props}
    className={clsx(props.className, "text-orange mb-mini mt-pico")}
  />
);

const Success = (props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "flex flex-col items-center justify-center py-midi md:py-giga",
    )}
  />
);

const Title = (props: any) => (
  <StyledH2
    {...props}
    className={clsx(props.className, "mb-mini text-center")}
  />
);

const Message = (props: any) => (
  <StyledP
    {...props}
    className={clsx(
      props.className,
      "mx-auto mb-mini text-center leading-caption",
    )}
  />
);

const ReviewsForm = withReviewsForm(
  ({
    className,
    data,
    errors,
    fitLength,
    fitWidth,
    gender,
    handleChange,
    handleFitLengthChange,
    handleFitWidthChange,
    handleOptionChange,
    handleRatingChange,
    handleSubmit,
    loading,
    rating,
    success,
  }) =>
    !success ? (
      <Wrapper className={className}>
        <Header>Leave a Review</Header>
        <Column>
          <Input
            data-testid="name"
            name="name"
            label="Name"
            value={data.name}
            handleChange={handleChange}
            error={!!errors["name"]}
            required
          />
          {errors["name"] ? (
            <ErrorMessage>{errors["name"]}</ErrorMessage>
          ) : null}
        </Column>
        <Column>
          <Input
            data-testid="email"
            name="email"
            label="Email"
            value={data.email}
            handleChange={handleChange}
            error={!!errors["email"]}
            required
          />
          {errors["email"] ? (
            <ErrorMessage>{errors["email"]}</ErrorMessage>
          ) : null}
        </Column>
        {process.env["GATSBY_FEATURE_REVIEWS_ENHANCEMENT"] &&
        (gender === "Mens" || gender === "Womens") ? (
          <FitFeedback
            fitLength={fitLength}
            fitWidth={fitWidth}
            errors={errors}
            gender={gender}
            handleFitLengthChange={handleFitLengthChange}
            handleFitWidthChange={handleFitWidthChange}
            handleSizeChange={handleOptionChange}
            handleTitleChange={handleChange}
            title={data.title}
          />
        ) : (
          <ReviewTitle
            data-testid="review-title"
            name="title"
            label="Review Title"
            value={data.title}
            handleChange={handleChange}
          />
        )}
        <TextareaWrapper>
          <Textarea
            data-testid="review-content"
            name="content"
            label="Leave a review"
            value={data.content}
            handleChange={handleChange}
            error={!!errors["content"]}
            required
          />
          {errors["content"] ? (
            <ErrorMessage>{errors["content"]}</ErrorMessage>
          ) : null}
        </TextareaWrapper>
        <Rating>
          <Stars>
            <Label id="rating" label="Rating" required />
            <div>
              {Array.from({ length: 5 }).map((_val, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleRatingChange(index + 1)}
                >
                  {index + 1 <= rating ? <IconFilledStar /> : <IconStar />}
                </button>
              ))}
            </div>
          </Stars>
          <ButtonWrapper>
            <Button
              onClick={handleSubmit}
              height="56"
              textSize="16"
              colour="primary"
              disabled={loading}
            >
              {loading ? "Submitting" : "Submit"}
            </Button>
          </ButtonWrapper>
        </Rating>
        {errors["unknown"] ? (
          <ErrorMessage>{errors["unknown"]}</ErrorMessage>
        ) : null}
      </Wrapper>
    ) : (
      <Success>
        <Title>Thanks for your review!</Title>
        <Message>
          We love hearing from you. Our team will publish it shortly.
        </Message>
      </Success>
    ),
);

export default ReviewsForm;

function IconFilledStar() {
  return (
    <svg
      fill="none"
      viewBox="0 0 18 17"
      className="mr-pico w-[20px] cursor-pointer text-primary"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.8672 0.581497C8.11233 -0.193833 9.21223 -0.193832 9.45735 0.581498L10.9027 5.15318C11.0127 5.50107 11.3371 5.73686 11.7028 5.73466L16.4842 5.70597C17.295 5.7011 17.6349 6.73671 16.9782 7.21113L13.0797 10.0276C12.786 10.2398 12.6635 10.617 12.7767 10.9606L14.2832 15.5366C14.5371 16.3075 13.6472 16.9475 12.9942 16.4637L9.15931 13.623C8.86419 13.4043 8.46036 13.4043 8.16525 13.623L4.3304 16.4637C3.67737 16.9475 2.7875 16.3075 3.04131 15.5366L4.5479 10.9606C4.66105 10.617 4.53853 10.2398 4.24485 10.0276L0.34632 7.21113C-0.310361 6.73671 0.0295399 5.7011 0.840333 5.70597L5.62176 5.73466C5.98743 5.73686 6.31186 5.50107 6.42185 5.15318L7.8672 0.581497Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconStar() {
  return (
    <svg
      fill="none"
      viewBox="0 0 36 35"
      className="mr-pico w-[20px] cursor-pointer text-primary"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18 2.61723L15.2514 11.3111C14.7577 12.8726 13.3037 13.9263 11.6699 13.9164L2.58095 13.8619L9.99191 19.2159C11.3071 20.1661 11.8582 21.8581 11.3501 23.4014L8.4854 32.1023L15.7767 26.7011C17.0971 25.7229 18.9029 25.7229 20.2233 26.7011L27.5146 32.1023L24.6499 23.4014C24.1418 21.8581 24.6929 20.1661 26.0081 19.2159L33.4191 13.8619L24.3301 13.9164C22.6963 13.9263 21.2423 12.8726 20.7486 11.3111L18 2.61723ZM19.6521 1.20834C19.1428 -0.402777 16.8572 -0.402781 16.3479 1.20834L13.3444 10.7082C13.1159 11.4311 12.4417 11.921 11.6819 11.9165L1.74619 11.8569C0.0613831 11.8467 -0.644922 13.9987 0.719645 14.9845L8.82069 20.8371C9.43096 21.278 9.68555 22.0618 9.45043 22.7759L6.31977 32.2846C5.79236 33.8865 7.64149 35.2165 8.99846 34.2112L16.9672 28.3082C17.5804 27.8539 18.4196 27.8539 19.0328 28.3082L27.0015 34.2112C28.3585 35.2165 30.2076 33.8865 29.6802 32.2846L26.5496 22.7759C26.3144 22.0618 26.569 21.278 27.1793 20.8371L35.2804 14.9845C36.6449 13.9987 35.9386 11.8467 34.2538 11.8569L24.3181 11.9165C23.5583 11.921 22.8841 11.4311 22.6556 10.7082L19.6521 1.20834Z"
        fill="currentColor"
      />
    </svg>
  );
}
